import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Metrics, type DomainSummary, type PathGroup } from '@/api/types';
import { metricValue } from '@/api/metric-resolution';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { CompareBars } from '@/components/CompareBars';
import { ExportButton } from '@/components/ExportButton';
import { RangeBar } from '@/components/RangeBar';
import { StatTile } from '@/components/StatTile';
import { formatCompact } from '@/components/TrendChart';
import { useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError } from '@/components/AnalyticsLoadError';

/**
 * The three domains, separated.
 *
 * <b>Why this page exists.</b> taviportal.com, jointavi.com and tavi.anyware.software share ONE GA4
 * property, so every other tab on this dashboard reports the three of them added together. "Which
 * domain is actually working" cannot be asked anywhere else.
 *
 * <b>GA4-only, and it says so.</b> Clarity has no host dimension and its 10-request daily budget
 * cannot fund a cross-tab, so there are no frustration metrics here. That is stated rather than
 * rendered as zeros — "0 rage clicks" is a claim the data does not make.
 */
export function DomainsPage({ siteKey }: { siteKey: string }) {
  const { days, setDays, from, to, setRange, isCustomRange, data: overview } = useOverview(siteKey);

  const domains = useQuery({
    queryKey: ['domains', siteKey, from, to],
    queryFn: () => api.domains(siteKey, from, to),
    retry: false,
    placeholderData: (previous) => previous,
  });

  const list = domains.data?.domains ?? [];
  const grandTotal = list.reduce((sum, d) => sum + sessionsOf(d), 0);

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={overview}
        setRange={setRange} isCustomRange={isCustomRange}
        actions={<ExportButton siteKey={siteKey} from={from} to={to} dimension="hostname" label="Export domains" />}
      />

      {domains.isError && <AnalyticsLoadError error={domains.error} what="domains" />}

      {domains.data && !domains.data.has_data && (
        <Callout tone="warning" title="No domain data yet">
          The <code>hostname</code> dimension was just added, and GA4 breakdowns only exist for days
          that have been ingested since. Run an ingestion from the Sources page — GA4 supports
          backfill, so past days can be recovered.
        </Callout>
      )}

      {list.length === 1 && list[0] && (
        <Callout tone="info" title="Only one domain is reporting">
          GA4 sees traffic on <code>{list[0].hostname}</code> only. If the other two are live, check
          that the same measurement ID is installed on them.
        </Callout>
      )}

      <div className="tile-grid">
        {list.map((domain) => (
          <StatTile
            key={domain.hostname}
            label={domain.hostname}
            value={formatCompact(sessionsOf(domain))}
            sub={grandTotal > 0 ? `${Math.round((sessionsOf(domain) / grandTotal) * 100)}% of sessions` : 'no sessions'}
          />
        ))}
      </div>

      {list.length > 1 && (
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>Sessions vs users</h2>
            <span className="t-label subtle">GA4 only — Clarity cannot break down by domain</span>
          </div>
          <div className="card-body">
            <CompareBars
              items={list.map((d) => ({
                label: d.hostname,
                value: sessionsOf(d),
                secondary: metricValue(d.metrics, 'users') ?? undefined,
              }))}
              valueLabel="Sessions"
              secondaryLabel="Users"
            />
          </div>
        </div>
      )}

      {list.length > 1 && (
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>All measures</h2>
            <span className="t-label subtle">exact numbers, for comparison down a column</span>
          </div>
          <div className="card-body" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th className="num">Sessions</th>
                  <th className="num">Users</th>
                  <th className="num">Page views</th>
                  <th className="num">Bounce</th>
                  <th className="num">Leads</th>
                  <th className="num">Share</th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.hostname}>
                    <td>{d.hostname}</td>
                    <td className="num">{num(sessionsOf(d))}</td>
                    <td className="num">{num(metricValue(d.metrics, 'users'))}</td>
                    <td className="num">{num(metricValue(d.metrics, 'pageViews'))}</td>
                    <td className="num">{pct(d.metrics[Metrics.bounceRate])}</td>
                    <td className="num">{num(d.metrics[Metrics.leads] ?? null)}</td>
                    <td className="num">
                      {grandTotal > 0 ? `${Math.round((sessionsOf(d) / grandTotal) * 100)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {domains.data && domains.data.path_groups.length > 0 && (
        <PathGroupCard
          title="Pages, with aliases folded"
          groups={domains.data.path_groups}
        />
      )}

      <div className="grid-2">
        {list.map((domain) => (
          <div className="card" key={`detail-${domain.hostname}`}>
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>{domain.hostname}</h2>
              <span className="t-label subtle">{num(sessionsOf(domain))} sessions</span>
            </div>
            <div className="card-body">
              <h3 className="t-label subtle">Top pages</h3>
              <BarList
                rows={domain.top_pages.slice(0, 8).map((g) => ({ label: g.canonical, value: g.total }))}
                emptyLabel="No page data for this domain"
              />
              <h3 className="t-label subtle" style={{ marginTop: 16 }}>Where they come from</h3>
              <BarList
                rows={domain.top_countries.slice(0, 6).map((c) => ({
                  label: c.value || '(not set)',
                  value: metricValue(c.metrics, 'sessions') ?? 0,
                }))}
                emptyLabel="No country data for this domain"
              />
              <h3 className="t-label subtle" style={{ marginTop: 16 }}>Channels</h3>
              <BarList
                rows={domain.top_channels.slice(0, 6).map((c) => ({
                  label: c.value || '(not set)',
                  value: metricValue(c.metrics, 'sessions') ?? 0,
                }))}
                emptyLabel="No channel data for this domain"
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * Canonical pages with their alias split.
 *
 * The variant line is the point of the card: it shows that "/" at 1,200 views is really
 * "/ 68% · /home 32%". A merge that cannot be inspected hides exactly the routing bug — half the
 * traffic landing on an alias that should redirect — that this report would otherwise reveal.
 */
function PathGroupCard({ title, groups }: { title: string; groups: PathGroup[] }) {
  const merged = groups.filter((g) => g.variants.length > 1);

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        <span className="t-label subtle">
          {merged.length > 0
            ? `${merged.length} page${merged.length === 1 ? '' : 's'} written more than one way`
            : 'no aliases found'}
        </span>
      </div>
      <div className="card-body" style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Page</th>
              <th className="num">Sessions</th>
              <th>Written as</th>
            </tr>
          </thead>
          <tbody>
            {groups.slice(0, 20).map((g) => (
              <tr key={g.canonical}>
                <td>{g.canonical}</td>
                <td className="num">{formatCompact(g.total)}</td>
                <td>
                  {g.variants.length === 1 ? (
                    <span className="subtle">—</span>
                  ) : (
                    <span className="t-label">
                      {g.variants.map((v, i) => (
                        <span key={v.path}>
                          {i > 0 && ' · '}
                          <code>{v.path}</code> {Math.round(v.share * 100)}%
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Sessions via the shared precedence chain — GA4 first, Clarity only if GA4 is absent. */
function sessionsOf(domain: DomainSummary): number {
  return metricValue(domain.metrics, 'sessions') ?? 0;
}

function num(value: number | null): string {
  return value === null ? '—' : formatCompact(value);
}

/** GA4's bounceRate is a 0–1 fraction (stored raw) — 0.45 is 45 %, never "0%". */
function pct(value: number | undefined): string {
  return value === undefined || !Number.isFinite(value) ? '—' : `${Math.round(value * 100)}%`;
}
